import { CellClickedEvent, GridApi, GridReadyEvent, ICellRendererParams } from '@ag-grid-community/core';
import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FieldExpression, NodeExpression, term, toString as luceneToString } from 'lucene';
import { fromEvent, Subscription } from 'rxjs';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../../../../shared/animations/page-transitions';
import { CommonColDef, RadioBtnColDef } from '../../../../../../../shared/datacenter/column-defs';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../../../../shared/datacenter/grid-options';
import { AddUserPageContent } from '../../../../../../../shared/models/add-user-info';
import { UserBasics } from '../../../../../../../shared/models/user-basics';
import { TranslationService } from '../../../../../../../shared/services/translation.service';
import { OrderBy, OrderByDirection } from '../../../../../../../shared/utils/constant';
import { Users } from '../../../../../../../users/models/user';
import { UserService } from '../../../../../../../users/services/user.service';
import { AddUserService } from '../../../../../../services/add-user.service';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/utils/loading-spinner.component';

const resizeObservable = fromEvent(globalThis, 'resize');
const SORT_WAY = OrderByDirection.ASC;
const SORT_BY = 'firstName';
@Component({
	selector: 'copo-step-matching-users',
	templateUrl: './step-matching-users.component.html',
	styleUrl: './step-matching-users.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,

		MatFormFieldModule,
		AgGridModule,
		LoadingSpinnerComponent,
	],
})

export class StepMatchingUsersComponent implements AddUserPageContent, OnDestroy, OnInit {
	@HostBinding('class.passthrough') readonly passthrough = true;
	userFields = ['id', 'firstName', 'lastName', 'status', 'address', 'personMembers.member.id'];
	firstName = '';
	lastName = '';
	email = '';
	sub?: Subscription;
	usersSub?: Subscription;
	matchingUsers: Users[] = [];
	newUser!: UserBasics;
	newUserRowData: UserBasics[] = [];
	isLoading = true;
	isAnyOneSelected?: boolean;
	nodeSelected?: number;
	isNewUser?: boolean;
	orgId?: string;
	gridApi?: GridApi;
	gridNewApi?: GridApi;
	selectedRow?: Users;
	currentSelectedUser?: Users;
	userAlreadyExist = false;
	emailIdExist = false;
	isActive = false;
	nextClicked: boolean;
	readonly modules = clientSideAgGridModules;
	gridOptions: CommonGridOptions<Users>;

	newUserGridOptions: CommonGridOptions<UserBasics>;

	private resizeSub?: Subscription;

	constructor(
		private readonly addUserService: AddUserService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService
	) {
		this.gridOptions = new CommonGridOptions<Users>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new CommonColDef<Users>({
					field: 'id',
					hide: true
				}),
				new RadioBtnColDef<Users>({
					headerName: '',
					colId: 'radioBtn',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					pinned: 'left',
					onCellClicked: event => {
						this.onUserSelection(event, false);
						event.api.refreshCells({ columns: [event.column] });
					},
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<unknown, unknown>) =>
							Boolean((Number(params.node.id) === this.nodeSelected) && !this.isNewUser)
					}
				}),

				new CommonColDef<Users>({
					headerName: this.translationService.getTR('add-user.name'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const userData = params.data;
						return `${ userData.firstName }  ${ userData.lastName }`;
					},
					fields: ['firstName', 'lastName'],
					width: 30
				}),
				new CommonColDef<Users>({
					headerName: this.translationService.getTR('add-user.email'),
					field: 'address.email',
				}),
			]
		});

		this.newUserGridOptions = new CommonGridOptions<UserBasics>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new RadioBtnColDef<UserBasics>({
					headerName: '',
					colId: 'radioBtn',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					pinned: 'left',
					onCellClicked: event => {
						this.onUserSelection(event, true);
						event.api.refreshCells({ columns: [event.column] });
					},
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<unknown, unknown>) =>
							Boolean((Number(params.node.id) === this.nodeSelected) && this.isNewUser)
					}
				}),

				new CommonColDef<UserBasics>({
					headerName: this.translationService.getTR('add-user.name'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const userData = params.data;
						return `${ userData.firstname || '' }  ${ userData.lastname || '' }`;
					},
					fields: ['firstname', 'lastname'],
					width: 30
				}),
				new CommonColDef<UserBasics>({
					headerName: this.translationService.getTR('add-user.email'),
					field: 'email',
				}),
			]
		});
		this.nextClicked = false;
		this.sub = this.addUserService.firstName.subscribe(firstName => {
			this.firstName = firstName;
		});
		this.sub = this.addUserService.lastName.subscribe(lastName => {
			this.lastName = lastName;
		});
		this.sub = this.addUserService.email.subscribe(email => {
			this.email = email;
		});
		this.sub = this.addUserService.isAnyOneSelected.subscribe(isAnyOneSelected => {
			this.isAnyOneSelected = isAnyOneSelected;
		});
		this.sub = this.addUserService.nodeSelected.subscribe(nodeSelected => {
			this.nodeSelected = nodeSelected;
		});
		this.sub = this.addUserService.isNewUserCreation.subscribe(newUser => {
			this.isNewUser = newUser;
		});
		this.sub = this.addUserService.currentSelectedUser.subscribe(currentSelectedUser => {
			this.currentSelectedUser = currentSelectedUser;
		});
		this.sub = this.addUserService.orgId.subscribe(orgId => {
			this.orgId = orgId;
		});
		this.newUser = {
			firstname: this.firstName,
			lastname: this.lastName,
			email: this.email,
		};
		this.newUserRowData.push(this.newUser);
		this.findMatchingUsers();
	}

	ngOnInit() {
		this.addUserService.emailIdExists(this.email).then(
			value => {
				this.addUserService.updateEmailIdExists(value);
			}
		).catch((err: unknown) => {
			console.error(err);
		});
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		if (this.usersSub !== undefined) {
			this.usersSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onNewGridReady(params: GridReadyEvent) {
		this.gridNewApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	findMatchingUsers() {
		this.usersSub = this.userService.getAllUsers(
			this.userFields,
			this.createFilter(),
			1,
			100,
			new OrderBy(SORT_BY, SORT_WAY),
			false
		).subscribe({
			next: usersData => {
				this.matchingUsers = usersData.results;
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('error loading', err);
			}

		});
	}

	createFilter() {
		const firstNameField: FieldExpression = {
			field: 'firstName',
			term: `*${ term.escape(this.firstName) }*`,
			quoted: false
		};
		const lastNameField: FieldExpression = {
			field: 'lastName',
			term: `*${ term.escape(this.lastName) }*`,
			quoted: false
		};
		const emailField: FieldExpression = {
			field: 'address.email',
			term: this.email,
			quoted: false
		};

		const nameNode: NodeExpression = {
			left: firstNameField,
			operator: 'AND',
			parenthesized: true,
			right: lastNameField
		};
		const finalNode: NodeExpression = {
			left: nameNode,
			operator: 'AND',
			parenthesized: true,
			right: emailField
		};
		return luceneToString(finalNode);
	}

	onUserSelection(event: CellClickedEvent<UserBasics> | CellClickedEvent<Users>, isNewUser: boolean) {
		if (!event.node.data) {
			return;
		}
		this.nextClicked = false;
		this.addUserService.updateIsAnyOneChoiceSelected(true);
		this.addUserService.updateNodeSelected(Number(event.node.id));
		this.addUserService.updateUserId(String(event.node.data.id));
		this.addUserService.updateIsNewUserCreation(isNewUser);
		if (!isNewUser) {
			this.selectedRow = <Users>event.node.data;
			this.addUserService.setSelectedUser(this.selectedRow);
			this.isActive = this.selectedRow.status.toLowerCase() === 'activ';
		} else {
			this.addUserService.setSelectedUser(undefined);
		}
	}

	checkErrors(): boolean | undefined {
		this.nextClicked = true;
		this.sub = this.addUserService.currentSelectedUser.subscribe(currentSelectedUser => {
			this.currentSelectedUser = currentSelectedUser;
		});
		const member = this.currentSelectedUser?.personMembers.find(pm => pm.member.id === Number(this.orgId));
		if (member) {
			this.userAlreadyExist = true;
		}
		this.isActive = this.currentSelectedUser?.status.toLowerCase() === 'activ';
		this.sub = this.addUserService.emailIdExistsCheck.subscribe(emailIdExists => {
			this.emailIdExist = emailIdExists;
		});
		return this.isNewUser ? this.isAnyOneSelected && !this.emailIdExist
			: !this.userAlreadyExist && this.isActive;
	}
}
